'use client';

import { useCallback, useEffect, useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  Button,
  FileUpload,
  IconButton,
  ImageWithSkeleton,
  SettingsEditDialog,
  Tooltip,
  formatFileSize,
  toasterApi,
} from '@terra/ui-design-system';
import { Delete02Icon } from '@terra/ui-icons';
import {
  WEBPAGE_SCREENSHOT_MAX_SIZE_BYTES,
  WEBPAGE_SCREENSHOT_MIME_TYPES,
} from '@terra/shared-contracts-web-pages';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../../clients/testerClient';

const SCREENSHOT_ACCEPT = WEBPAGE_SCREENSHOT_MIME_TYPES.join(',');
const SCREENSHOT_MAX_SIZE_LABEL = formatFileSize(
  WEBPAGE_SCREENSHOT_MAX_SIZE_BYTES,
);

type FileUploadRejectDetails = {
  files: Array<{
    file: File;
    errors: string[];
  }>;
};
type ReplaceWebpageScreenshotDialogProps = {
  open: boolean;
  webpageId: string;
  onOpenChange: (open: boolean) => void;
  onScreenshotReplaced?: () => void | Promise<void>;
};

export function ReplaceWebpageScreenshotDialog({
  open,
  webpageId,
  onOpenChange,
  onScreenshotReplaced,
}: ReplaceWebpageScreenshotDialogProps) {
  const tCommon = useTranslations('designSystem.common');
  const tAssets = useTranslations('designSystem.applicationAssets');
  const tFileUpload = useTranslations('designSystem.chat.fileUpload');
  const editLabel = tCommon('edit');
  const removeFileLabel = tFileUpload('removeFile');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const resetSelection = useCallback(() => {
    setSelectedFile(null);
    setFileUploadKey((key) => key + 1);
  }, []);
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (!nextOpen) {
        resetSelection();
      }
    },
    [onOpenChange, resetSelection],
  );

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);

      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);

    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);
  const handleFileAccept = useCallback((details: { files: File[] }) => {
    setSelectedFile(details.files[0] ?? null);
  }, []);
  const handleFileReject = useCallback(
    (details: FileUploadRejectDetails) => {
      const rejected = details.files[0];

      if (!rejected) {
        return;
      }

      if (rejected.errors.includes('FILE_TOO_LARGE')) {
        toasterApi.error({
          title: tFileUpload('uploadError'),
          description: tFileUpload('fileTooLarge', {
            fileName: rejected.file.name,
            maxSize: SCREENSHOT_MAX_SIZE_LABEL,
          }),
        });

        return;
      }

      toasterApi.error({
        title: tFileUpload('uploadError'),
        description: tAssets('sections.invalidScreenshotFile', {
          fileName: rejected.file.name,
          maxSize: SCREENSHOT_MAX_SIZE_LABEL,
        }),
      });
    },
    [tAssets, tFileUpload],
  );
  const handleReplaceSubmit = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    setIsUploading(true);

    try {
      await terraProxyApiClient.replaceWebpageScreenshot(
        webpageId,
        selectedFile,
      );
      toasterApi.success({
        title: tFileUpload('uploadSuccess'),
      });
      handleOpenChange(false);
      await onScreenshotReplaced?.();
    } catch (error) {
      console.error('Failed to replace webpage screenshot:', error);
      toasterApi.error({
        title: tFileUpload('uploadError'),
      });
    } finally {
      setIsUploading(false);
    }
  }, [
    handleOpenChange,
    onScreenshotReplaced,
    selectedFile,
    tFileUpload,
    webpageId,
  ]);

  return (
    <SettingsEditDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={editLabel}
      description={tAssets('sections.editScreenshotDescription')}
      onSubmit={handleReplaceSubmit}
      loading={isUploading}
      isSubmitDisabled={!selectedFile}
      contentMaxW="360px"
    >
      <FileUpload.Root
        key={fileUploadKey}
        accept={SCREENSHOT_ACCEPT}
        maxFiles={1}
        maxFileSize={WEBPAGE_SCREENSHOT_MAX_SIZE_BYTES}
        onFileAccept={handleFileAccept}
        onFileReject={handleFileReject}
        w="full"
      >
        {selectedFile && previewUrl ? (
          <VStack gap={3} alignItems="stretch" w="full">
            <Box
              borderRadius="md"
              overflow="hidden"
              borderWidth={1}
              borderColor="border.subtle"
              maxH="200px"
              bg="bg.subtle"
            >
              <ImageWithSkeleton
                src={previewUrl}
                alt={selectedFile.name}
                errorMessage={tAssets('cards.imageLoadError')}
                objectFit="contain"
              />
            </Box>
            <HStack justifyContent="space-between" alignItems="center" gap={2}>
              <BodyDefault color="fg.muted" truncate>
                {selectedFile.name}
              </BodyDefault>
              <Tooltip title={removeFileLabel}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={removeFileLabel}
                  disabled={isUploading}
                  onClick={resetSelection}
                >
                  <Delete02Icon size={12} strokeWidth={1.5} />
                </IconButton>
              </Tooltip>
            </HStack>
          </VStack>
        ) : (
          <FileUpload.Dropzone
            w="full"
            p={3}
            borderRadius="md"
            minH="auto"
            borderWidth={1}
            borderStyle="dashed"
            borderColor="border.subtle"
          >
            <VStack gap={2} alignItems="center">
              <FileUpload.Trigger asChild>
                <Button size="xs" variant="outline">
                  {tFileUpload('attachFiles')}
                </Button>
              </FileUpload.Trigger>
              <BodyDefault color="fg.muted">
                {tAssets('sections.screenshotUploadHint', {
                  maxSize: SCREENSHOT_MAX_SIZE_LABEL,
                })}
              </BodyDefault>
            </VStack>
          </FileUpload.Dropzone>
        )}
        <FileUpload.HiddenInput />
      </FileUpload.Root>
    </SettingsEditDialog>
  );
}
